<template>
  <v-main>
    <navigation/>

    <v-container class="my-8">
      <div class="grid">
        <!-- Doctor Info -->
        <doctor-basic-info-section
          class="grid-column-1-2"
          :doctor="doctor"
          :user-info="userInfo"
          :has-adv-permission="hasAdvPermission"
          :has-guide-line-permission="hasGuideLinePermission"
          :banner-loading="bannerLoading"
          @upload-banner="uploadDoctorBanner"
          @photo-settings-saved="getDoctor"
          @update-info="updateDoctorInfo"
        />

        <!-- Doctor Schedule -->
        <div class="grid-column-2-3 row-span-3">
          <doctor-schedule-section :doctor="doctor" @update="updateSchedule"/>
        </div>

        <!-- Health Education Sharing Section -->
        <div id="HealthSection" class="grid-column-1-2">
          <health-education-sharing :has-permission="hasAdvPermission"/>
        </div>

        <!-- Short Video Section -->
        <div class="grid-column-1-2">
          <ShortVideoCard :has-permission="hasAdvPermission"/>
        </div>

        <div class="grid-column-1-2">
          <RegularVideoCard :has-permission="hasAdvPermission"/>
        </div>

        <!-- Treatment Section -->
        <doctor-treatment-section
          :doctor="doctor"
          :dentco-icons="dentcoIcons"
          :has-adv-permission="hasAdvPermission"
          @update-hashtag="updateHashTag"
        />

        <!-- Medical Qualifications + Educational Background Section -->
        <doctor-education-section
          class="grid-column-1-2"
          :doctor="doctor"
          @update-qualifications="updateQualifications"
          @update-education="updateEducation"
        />

        <!-- Clinic Concept Section -->
        <doctor-concept-section
          class="grid-column-1-2"
          :doctor="doctor"
          :has-adv-permission="hasAdvPermission"
          @update-principle="updatePrinciple"
        />
      </div>
    </v-container>
  </v-main>
</template>

<script>
import Navigation from "@/components/Navigation.vue";
import DoctorBasicInfoSection from "@/pages/settings/doctor/sections/DoctorBasicInfoSection.vue";
import DoctorScheduleSection from "@/pages/settings/doctor/sections/DoctorScheduleSection.vue";
import DoctorTreatmentSection from "@/pages/settings/doctor/sections/DoctorTreatmentSection.vue";
import DoctorEducationSection from "@/pages/settings/doctor/sections/DoctorEducationSection.vue";
import DoctorConceptSection from "@/pages/settings/doctor/sections/DoctorConceptSection.vue";
import HealthEducationSharing from "@/components/settings/doctor/v2/HealthEducationSharing.vue";
import ShortVideoCard from "@/components/settings/doctor/v2/ShortVideoCard.vue";
import RegularVideoCard from "@/components/settings/doctor/v2/RegularVideoCard.vue";
import { mapGetters } from "vuex";
import {
  getDentcoIconsAPI,
  getDoctorInfoAPI, getInfoAPI,
  updateDoctorClinicAPI,
  updateDoctorInfoAPI
} from "@/api";
import { checkPermissions, transformSchedule } from "@/utils/tool";
import { doctor as mockDoctor, permissionInfo } from "@/api/mockData/tour/onboarding";

export default {
  name: "DoctorSettingContainer",

  components: {
    Navigation,
    DoctorBasicInfoSection,
    DoctorScheduleSection,
    DoctorTreatmentSection,
    DoctorEducationSection,
    DoctorConceptSection,
    HealthEducationSharing,
    ShortVideoCard,
    RegularVideoCard,
  },

  data() {
    return {
      userInfo: null,
      doctor: {},
      bannerLoading: false,
      dentcoIcons: [],
    };
  },

  computed: {
    ...mapGetters([
      'axiosParamData'
    ]),
    ...mapGetters('tour', {
      currentTourName: 'currentTourName'
    }),
    hasAdvPermission() {
      return checkPermissions({obj: this.userInfo, type: 'doctor', permissions: ['adv_page']})
    },
    hasGuideLinePermission() {
      return checkPermissions({obj: this.userInfo, type: 'doctor', permissions: ['guide_to_line']})
    },
  },

  mounted() {
    if (this.currentTourName === 'onboarding') {
      this.doctor = mockDoctor
    } else {
      this.getDoctor()
    }
    this.getPermissionsInfo()
    this.getDentcoIcons()
  },

  methods: {
    transformSchedule,
    async getDoctor() {
      try {
        const response = await getDoctorInfoAPI(this.axiosParamData);
        const formattedClinics = response.data.clinics.map(clinic => ({
          ...clinic,
          schedule: this.transformSchedule(clinic.doctor_open_date)
        }));
        this.doctor = {...response.data, clinics: formattedClinics};
      } catch (error) {
        console.error('Error fetching doctor data:', error);
      }
    },
    uploadDoctorBanner(file) {
      this.bannerLoading = true;
      const data = {
        ...this.axiosParamData
      }
      const formData = new FormData()
      for (const [key, value] of Object.entries(data)) {
        formData.append(key, value)
      }
      formData.append('banner', file)
      updateDoctorInfoAPI(formData).then(() => {
      }).catch(() => {
        this.$notify.error('上傳失敗')
      }).finally(() => {
        this.getDoctor()
        this.bannerLoading = false;
      })
    },
    async updateDoctorInfo(payload) {
      const {data, callback} = payload;
      const {alias, email, social_item_structure, clinics} = data;

      try {
        await Promise.all([
          updateDoctorInfoAPI({
            alias,
            email,
            social_item_structure,
            ...this.axiosParamData
          }),
          updateDoctorClinicAPI({
            clinics,
            ...this.axiosParamData
          }),
        ]);
        // 別名會影響簡訊前綴，存檔後重新載入 userInfo
        this.$store.dispatch('loadUserInfo', { force: true }).catch(() => {});

        if (callback && typeof callback === 'function') {
          callback('success');
        }
      } catch (error) {
        if (callback && typeof callback === 'function') {
          callback('error');
        }
        console.error(error);
      } finally {
        this.getDoctor();
      }
    },

    async handleUpdateAPI({data, apiMethod, callback}) {
      try {
        await apiMethod({
          ...data,
          ...this.axiosParamData
        });
        if (callback && typeof callback === 'function') {
          callback('success');
        }
      } catch (error) {
        if (callback && typeof callback === 'function') {
          callback('error');
        }
        console.error(error);
      } finally {
        this.getDoctor();
      }
    },
    async updateHashTag(payload) {
      const {hashtags, callback} = payload;
      await this.handleUpdateAPI({
        data: {hashtag_item_structure: hashtags},
        apiMethod: updateDoctorInfoAPI,
        callback,
      });
    },
    async updateSchedule(payload) {
      const {clinics, callback} = payload;
      await this.handleUpdateAPI({
        data: {clinics},
        apiMethod: updateDoctorInfoAPI,
        callback,
      });
    },
    async updateQualifications(payload) {
      const {qualification, callback} = payload;
      await this.handleUpdateAPI({
        data: {qualification},
        apiMethod: updateDoctorInfoAPI,
        callback,
      });
    },
    async updateEducation(payload) {
      const {education, callback} = payload;
      await this.handleUpdateAPI({
        data: {education},
        apiMethod: updateDoctorInfoAPI,
        callback,
      })
    },
    async updatePrinciple(payload) {
      const {principle, callback} = payload;
      await this.handleUpdateAPI({
        data: {principle},
        apiMethod: updateDoctorInfoAPI,
        callback,
      })
    },

    getDentcoIcons() {
      getDentcoIconsAPI(this.axiosParamData).then(res => {
        this.dentcoIcons = res.data
      })
    },
    getPermissionsInfo() {
      if(this.currentTourName === 'onboarding') {
        this.userInfo = permissionInfo
      } else {
        getInfoAPI(this.axiosParamData).then(res => {
          this.userInfo = res.data
        })
      }
    },
  }
}
</script>

<style scoped lang="scss">
.grid {
  display: grid;
  gap: 2rem;
  grid-template-columns: 100%;
}

.grid-column-1-2,
.grid-column-2-3 {
  grid-column: 1 / -1;
}

.row-span-3 {
  grid-row: span 3;
}

@media (min-width: 768px) {
  .grid {
    grid-template-columns: calc(100% - 382px) 350px;
  }
  .grid-column-1-2 {
    grid-column: 1 / 2; /* 等價於 md:col-start-1 md:col-end-2 */
  }
  .grid-column-2-3 {
    grid-column: 2 / 3; /* 等價於 md:col-start-2 md:col-end-3 */
  }
}
</style>
